import { QueryOptions } from '@apollo/client/core';
import BaseService from '@root/common/base/BaseService';
import { ServiceInterface } from '@root/common/types/service';
import { SectionsData } from '@root/modules/feed/types/pageContent';
import getSectionContentQuery from '@root/modules/feed/graphql/getSectionContentQuery.graphql';
import { FeedAdapter } from '@root/modules/feed/utils/FeedAdapter';
import { dataLoader, RedisDataLoaderOptions } from '@root/libs/redis/dataloader/dataLoader';

type ServiceVariables = {
  id: any;
};

export default class GetSectionContentService extends BaseService implements ServiceInterface {
  private async fetchSectionContent(variables: ServiceVariables) {
    const options: QueryOptions<ServiceVariables> = Object.assign({ query: getSectionContentQuery }, { variables });

    const dataLoaderOptions: RedisDataLoaderOptions = {
      remote: {
        expireTimeMs: 30000, // 30 seconds
        gracePeriodMs: 24 * 3600 * 1000, // 24 hours
        keyPrefix: 'section_feed',
      },
    };

    const requestWrapper = async (options: QueryOptions<ServiceVariables>): Promise<SectionsData | Error> => {
      const apiProvider = this.createProvider('GraphQL');
      apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

      const [response] = await apiProvider.query<SectionsData>(options);

      this.throwGraphqlOrApolloErrorIfExists(response);

      const isSectionsEmpty = !response.data.sections[0]?.id;

      // Throw error if no section found
      if (isSectionsEmpty) {
        throw new Error('sectionContent.service requestWrapper response error');
      }

      return response.data;
    };

    const redisDataLoader = dataLoader<QueryOptions<ServiceVariables>, SectionsData | Error>(requestWrapper, dataLoaderOptions);
    const response: SectionsData | Error = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

    if (response instanceof Error) {
      throw response;
    }

    return {
      sections: response?.sections,
      pager: [],
    };
  }

  public async fetch(variables: ServiceVariables) {
    try {
      const response = await this.fetchSectionContent(variables);
      const feedAdapter = new FeedAdapter(response.sections);
      const adaptedFeed = feedAdapter.adapt();
      return { sections: adaptedFeed, pager: [] };
    } catch (error) {
      process.sentry?.captureException(`GetSectionContentService fetch failed`, {
        contexts: { data: { variables }, ...(error.message && { message: error.message }), ...(error.cause && { cause: error.cause }) },
        tags: { 'process.type': process.server ? 'server' : 'client' },
      });
      this.handleError({ statusCode: 500, message: 'error.unexpected_system_error' });
    }
  }
}

export const sectionContentService = new GetSectionContentService();

export function fetchSectionContentService(variables: ServiceVariables, { req }: { req: PortalRootIncomingMessage }) {
  const sectionContentService = new GetSectionContentService({ req });
  return sectionContentService.fetch(variables);
}
